import { Link } from "@tanstack/react-router";
import logoAsset from "@/assets/monroya-logo.png.asset.json";
import logoDarkAsset from "@/assets/monroya-logo-dark.png.asset.json";

// Source asset is 1536×1024 with significant transparent padding.
// The actual logo content bbox is x:255–1223, y:389–609 → 968×220 (≈ 4.4:1).
// We render the asset as a positioned background so the visible content
// fills the wrapper exactly — no padding, no overflow — at any height.
const IMG_W = 1536;
const IMG_H = 1024;
const CONTENT_X = 255;
const CONTENT_Y = 389;
const CONTENT_W = 968;
const CONTENT_H = 220;
const ASPECT = CONTENT_W / CONTENT_H; // ~4.4

export function Logo({
  to = "/",
  className = "",
  height = 56,
  invert = false,
  forceLight = false,
}: {
  to?: string;
  className?: string;
  /** Rendered height of the visible logo in px. Width is derived from aspect. */
  height?: number;
  /** Force white lettering (for dark backgrounds). */
  invert?: boolean;
  /** Always render the light-mode logo, ignoring dark mode. */
  forceLight?: boolean;
}) {
  const width = height * ASPECT;
  const bgW = (IMG_W / CONTENT_W) * width;
  const bgH = (IMG_H / CONTENT_H) * height;
  const bgX = -(CONTENT_X / IMG_W) * bgW;
  const bgY = -(CONTENT_Y / IMG_H) * bgH;

  // Boundary between mark (x:255-505) and wordmark (x:534-1223) within the
  // content box. Use the midpoint of the gap so neither side is clipped.
  // (520 - 255) / 968 ≈ 0.2738
  const LETTER_START_PCT = ((520 - CONTENT_X) / CONTENT_W) * 100;

  const bgStyle: React.CSSProperties = {
    width: `${width}px`,
    height: `${height}px`,
    backgroundImage: `url(${logoAsset.url})`,
    backgroundSize: `${bgW}px ${bgH}px`,
    backgroundPosition: `${bgX}px ${bgY}px`,
    backgroundRepeat: "no-repeat",
  };

  return (
    <Link
      to={to}
      className={`inline-block shrink-0 ${className}`}
      aria-label="Monroya home"
    >
      {invert ? (
        <div
          role="img"
          aria-label="Monroya brand logo"
          className="relative"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          {/* Mark — keep original colors */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              ...bgStyle,
              clipPath: `inset(0 ${100 - LETTER_START_PCT}% 0 0)`,
            }}
          />
          {/* Lettering — inverted to white */}
          <div
            aria-hidden
            className="absolute inset-0 brightness-0 invert"
            style={{
              ...bgStyle,
              clipPath: `inset(0 0 0 ${LETTER_START_PCT}%)`,
            }}
          />
        </div>
      ) : forceLight ? (
        <div
          role="img"
          aria-label="Monroya brand logo"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <div aria-hidden style={bgStyle} />
        </div>
      ) : (
        <div
          role="img"
          aria-label="Monroya brand logo"
          className="relative"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <div aria-hidden className="absolute inset-0 dark:hidden" style={bgStyle} />
          <img
            aria-hidden
            src={logoDarkAsset.url}
            alt=""
            className="absolute inset-0 hidden h-full w-full object-contain object-left dark:block"
          />
        </div>
      )}
    </Link>
  );
}

/** Just the triangular mark, rendered as SVG so it scales cleanly and inherits
 * gradient at small sizes (used inside the priority-score badge). */
export function LogoMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <defs>
        <linearGradient id="monroya-mark-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6BCBF6" />
          <stop offset="100%" stopColor="#3B4FE0" />
        </linearGradient>
      </defs>
      <path
        d="M12 3 L21 20 L17 20 L12 11 L7 20 L3 20 Z"
        fill="url(#monroya-mark-grad)"
      />
    </svg>
  );
}
